import {
  csx,
  Modal,
  ModalButton,
  ModalContent,
  ModalDismiss,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Text,
  type ModalProps,
} from '@vtex/admin-ui'
import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Event } from 'typings'
import { useAnalytics } from 'use-analytics'
import { messages } from './messages'
import { styles } from './styles'
import { ModalConfirmButton } from './ModalConfirmButton'

export interface ReviewModalProps extends ModalProps {
  onConfirm: () => Promise<void>
  onCancel: () => void
  children?: React.ReactNode
  helpText?: React.ReactNode
  loading?: boolean
  holdToSave?: boolean
}

export function ReviewModal(props: ReviewModalProps) {
  const {
    onConfirm,
    onCancel,
    children,
    state,
    helpText,
    loading: customLoading = false,
    holdToSave = false,
    ...rest
  } = props

  const { track } = useAnalytics()

  const [reviewStartTime, setReviewStartTime] = useState<Date>(new Date())

  const [loading, setLoading] = useState(false)

  function handleConfirm() {
    setLoading(true)

    onConfirm().finally(() => {
      setLoading(false)
      state.hide()
    })
  }

  useEffect(() => {
    if (state.open) {
      track(Event.PROMOTION_REVIEW_STARTED)

      setReviewStartTime(new Date())
    }
  }, [state.open, track])

  const getElapsedTime = () =>
    (new Date().getTime() - reviewStartTime.getTime()) / 1000

  return (
    <Modal state={state} size="medium" {...rest}>
      <ModalHeader>
        <ModalTitle>
          <FormattedMessage {...messages.modal.title} />
        </ModalTitle>
        <ModalDismiss />
      </ModalHeader>

      <ModalContent>{children}</ModalContent>

      <ModalFooter className={styles.footer}>
        <Text variant="action2" tone="secondary">
          {helpText}
        </Text>
        <div className={styles.footerButtons}>
          <ModalButton
            variant="secondary"
            dismissModal
            onClick={() => {
              track(Event.PROMOTION_REVIEW_ABANDONED, {
                reviewingTime: getElapsedTime(),
              })
              onCancel()
            }}
            disabled={loading}
          >
            <FormattedMessage {...messages.modal.cancel} />
          </ModalButton>
          <ModalConfirmButton
            holdToSave={holdToSave}
            loading={loading || customLoading}
            onClick={() => {
              handleConfirm()

              track(Event.PROMOTION_REVIEW_FINISHED, {
                reviewingTime: getElapsedTime(),
              })
            }}
          />
        </div>
      </ModalFooter>
    </Modal>
  )
}
